// Content data for the website
export const siteContent = {
  siteName: "Faith by Experiments",
  tagline: "Faith, tested in real life.",

  contact: {
    email: "ajitkumar.faithbyexperiments@gmail.com",
    whatsappDisplay: "+91 72909 86763",
    whatsappE164: "+917290986763",
  },

  hero: {
    headline: "Faith that fears testing is not faith.",
    supportingText: [
      "Faith treated as a hypothesis—tested in real life.",
      "A private body of work for people who think before they believe.",
    ],
  },

  aboutFounder: {
    title: "About the Founder",
    content: `Ajit Kumar is a mechanical engineer by training, a systems thinker by practice, and an experimentalist by temperament. With over two decades of professional experience in large-scale industrial environments—spanning petroleum refineries, petrochemical complexes, and complex project ecosystems—Ajit has spent his career solving problems where assumptions fail and evidence matters. His work has demanded rigor, data-driven reasoning, and the humility to test ideas against reality—qualities that now define his approach to questions of faith, meaning, and inner life.

Educated as an engineer and certified as a project management professional, Ajit's intellectual journey later expanded into artificial intelligence, applied data science, and generative models, where he continues to explore how complex systems learn, adapt, and evolve.

Across engineering, AI, and leadership roles, one pattern became clear: beliefs that are not tested eventually break—whether in machines, organizations, or human lives. Faith by Experiments was born from this realization. Rather than asking people to believe first, this platform invites them to observe, test, reflect, and verify—using life itself as the laboratory.

Drawing inspiration from scientific thinking, lived experience, and timeless philosophical insights, Ajit approaches faith not as dogma, but as a hypothesis worth examining.

This venture is especially designed for:

• Engineers, scientists, professionals, and rational thinkers
• Individuals disillusioned by blind belief yet unsatisfied by material explanations alone
• Seekers who value intellectual honesty over certainty

Ajit does not claim final answers. Instead, he offers structured experiments, reflective frameworks, and real-world observations that allow readers to test deeper questions for themselves:

• Does intention influence outcomes?
• Can disciplined inner practices produce measurable external change?
• Is meaning an illusion—or an emergent property of lived experimentation?

At its core, Faith by Experiments is not about converting minds—it is about engaging them. Ajit believes that when faith is approached with the same seriousness as science—open to falsification, grounded in experience, and free from fear—it becomes not weaker, but profoundly stronger.`,
    imageUrl:
      "https://customer-assets.emergentagent.com/job_34e2cbef-ee34-45ac-8348-79293beec714/artifacts/w20t1x53_Production-%20Photo_Ajit%20Kumar.png",
  },

  positioningBridge: {
    statement:
      "This work stands between unquestioned belief and unexamined doubt. Faith, treated seriously, must be tested.",
  },

  manifesto: {
    title: "The Faith by Experiments Manifesto",
    sections: [
      {
        heading: "This Is Not Another Belief System",
        content:
          "Belief systems ask for loyalty. This platform asks for <strong>evidence</strong>.\n\nFaith by Experiments exists because blind belief has failed educated minds—and sterile rationalism has failed the human spirit. Between superstition and skepticism lies a neglected territory:<strong> faith that can be tested</strong>.\n\n This is that territory.",
      },
      {
        heading: "We Reject Comfortable Answers",
        content:
          "If an idea cannot survive questioning, it does not deserve reverence.\n\n In engineering, false assumptions break machines.\n In organizations, they destroy value.\n In life, they quietly erode meaning.\n\n Yet faith is often declared exempt from scrutiny.\n\n We reject that exemption.\n\n Here, faith is not inherited, defended, or protected.\n It is <strong>placed under pressure</strong>.",
      },
      {
        heading: "Faith Is a Hypothesis, Not a Conclusion",
        content:
          "Every serious inquiry begins with a hypothesis.\n What if intention matters?\n What if disciplined inner practices create observable external change?\n What if meaning is not imagined—but discovered through experimentation?\n\n These are not beliefs.\n They are <strong>testable propositions</strong>.\n\n Life is the laboratory.\n Experience is the data.",
      },
      {
        heading: "This Space Is Designed for Educated Minds",
        content:
          "This platform is not for those seeking comfort.\n It is for those seeking <strong>coherence</strong>.\n Engineers.\n Scientists.\n Professionals.\n Thinkers who respect evidence—and still feel that something essential is missing from purely material explanations.\n\n If you demand rigor, clarity, and intellectual honesty, you belong here.",
      },
      {
        heading: "No Gurus. No Dogma. No Final Answers",
        content:
          "I do not offer revelations.\n I offer <strong>frameworks</strong>.\n\n I do not promise certainty.\n I promise <strong>experiments worth running</strong>.\n\n Authority here does not come from tradition or charisma.\n It comes from <strong>repeatable insight</strong>.\n\n If an idea works only once, it is coincidence.\n If it works across lives, contexts, and time—it demands attention.",
      },
      {
        heading: "Depth Over Virality. Substance Over Noise",
        content:
          "This is a slow platform in a fast world.\n\n We value:\n• Thought over opinion\n• Precision over motivation\n• Transformation over consumption\n\nMuch of what is shared here is <strong>not meant for everyone</strong>.\n It is meant for those willing to think, test, and reflect.",
      },
      {
        heading: "The Invitation",
        content:
          "Do not believe what you read here.\n\n <strong>Test it.</strong>\n\n Run the experiments.\n Observe the outcomes.\n Discard what fails.\n Keep what works.\n\n Faith, when subjected to honest experimentation, does not weaken.\n\n It either collapses—or evolves.\n Faith by Experiments exists for those unafraid of either outcome.",
      },
      {
        heading: "Who This Is Not For",
        content:
          "This space is <strong>not</strong> for everyone—and that is intentional.\n\n This is not for you if:\n • You want ready-made answers instead of questions worth living with\n • You are looking for motivation, affirmations, or spiritual entertainment\n • You defend beliefs because they are inherited, popular, or emotionally safe\n • You prefer certainty over honesty\n\n <strong>Faith by Experiments is for those willing to be wrong, revise assumptions, and test ideas patiently.</strong>\n\n If that discomfort feels unnecessary, this platform will not resonate.",
      },
      {
        heading: "Why This Is a Premium Space",
        content:
          'This is a <strong>premium space</strong> because depth demands time, independence, and rigor.\n\n Truth-seeking has always been a minority pursuit and serious inquiry requires commitment.\n This is not content for casual scrolling.\n It is work—for the mind and the inner life.\n\n A paid subscription grants access to:\n • Original concepts of "Faith by experiments", "Synchronized Faith" and "Synchronized action"\n • Original experimental frameworks\n • Structured practices designed to be tested over time\n • Twelve months of uninterrupted access to all current and newly published premium content.\n\nIf you are looking for quick answers, this is not for you. \n\n If you are willing to <strong>earn insight</strong>, welcome.\n You are not paying for content. You are investing in <strong>intellectual integrity and inner experimentation.</strong>',
      },
    ],
  },

  workStructure: {
    title: "How the Work is Structured",
    items: [
      'Concept of "Faith by experiments"',
      "Lab experiments conducted to show the effects of spiritualism on brain and the health benefits",
      'Concept of "Synchronized Faith" and the flowchart describing various elements',
      "Activities to generate synchronized faith",
      'Concept of "Synchronized action"',
    ],
  },

  pricing: {
    title: "Join The Faith by Experiments Journey",
    valuePoints: [],
    quote: "Faith, treated seriously, must be tested.",
    plans: [
      {
        name: "Monthly Subscription",
        price: "499",
        currency: "₹",
        period: "month",
        note: "Cancel anytime",
        recommended: false,
      },
      {
        name: "Yearly Subscription",
        price: "4,999",
        currency: "₹",
        period: "year",
        note: "Cancel anytime",
        recommended: false,
        badge: "",
      },
    ],
    cta: "Subscribe here",
  },

  flagshipPreview: {
    title: "Flagship Contents Preview",
    previewContent: `Scholars estimate that there are <strong>several thousand distinct religions</strong> around the world — commonly cited at over 4,000 — ranging from major global traditions to local indigenous practices. It exists because, at different points in history, individuals realized that the practices they inherited were <strong>not producing results</strong> in their own lives. These individuals were not rebels by default. They were curious, dissatisfied, and willing to search for meaning that actually <strong>worked</strong> for them. What they discovered eventually became systems of faith—methods, disciplines, and practices that aligned with their lived experience rather than inherited expectation. Most of us never pause to notice this. We continue with familiar practices, hoping they will someday deliver different outcomes. Often, they do not. The uncomfortable truth is that <strong>faith cannot be outsourced</strong>. Each individual must arrive at a system that resonates with their own mind, temperament, and lived reality. This has always been true. Every person who discovered a transformative form of faith did so by <strong>finding their own way</strong>—not by copying outcomes, but by discovering principles. The question then arises: must one withdraw from the world, sit in isolation, or spend years in seclusion to arrive at such clarity? Not necessarily. What unites most of these seekers was not withdrawal, but <strong>experimentation</strong>. They questioned what they were taught, tested alternatives, observed results, and refined their understanding. Experimentation—not inheritance—was the common thread. From my experience, I am convinced that <strong>every human being is capable of this</strong>. Over the last 26 years, beginning at the age of nineteen, I have explored these questions through sustained personal experimentation, alongside the study of over a hundred books—including foundational texts from multiple religious traditions—and relevant scientific research from across the world. From this body of inquiry, I have distilled a sequence of steps: structured, testable, and grounded in lived experience. What follows is not belief. It is an experiment.`,
    lockedMessage:
      "Continue reading by subscribing to access the complete experimental framework and all premium content.",
  },
};
